/**
 * Browser-side observer wiring — ports BHK/src/app.js:776-881 verbatim.
 *
 * Three observers, all wired on DOMContentLoaded:
 *   - lead_form_started: focusin on first input/select/textarea per form per session
 *   - section_viewed:    IntersectionObserver ≥50%, fires once per section per page
 *   - page_left:         pagehide signal w/ last section, max scroll, time on page
 *
 * Public API:
 *   window.installTrackingObservers()  — idempotent; called once on DOMContentLoaded
 */
(function (root) {
  'use strict';

  /* ────────────────────────────────────────────────
     lead_form_started — fires once per form per session
     on first focus into a real input/select/textarea.
     Decouples "click apply CTA" (cta_clicked) from
     "actually started filling form" (lead_form_started).
  ──────────────────────────────────────────────── */
  function _setupLeadFormStartedTracking() {
    document.querySelectorAll('[data-form-id]').forEach(form => {
      const formId = form.dataset.formId;
      if (!formId) return;
      const sessionKey = 'lf_started_' + formId;
      form.addEventListener('focusin', e => {
        try {
          if (sessionStorage.getItem(sessionKey)) return;
          if (!e.target.matches('input, select, textarea')) return;
          sessionStorage.setItem(sessionKey, '1');
          if (window.trackedCapture) {
            window.trackedCapture('lead_form started', { form: formId });
          }
        } catch (err) { /* analytics never breaks UX */ }
      });
    });
  }

  /* ────────────────────────────────────────────────
     section_viewed — fires once per section per pageview when
     element reaches 50% viewport visibility. Direct posthog.capture,
     no GTM trigger. Pre-existing [data-section-name] hooks site-wide
     from Phase 1 are the input source.
  ──────────────────────────────────────────────── */
  function _setupSectionViewedTracking() {
    if (!window.trackedCapture) return;
    if (!('IntersectionObserver' in window)) return;

    const sections = document.querySelectorAll('[data-section-name]');
    if (!sections.length) return;

    const fired = new Set();  // page-load scoped, fire once per section name

    const io = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (!e.isIntersecting || e.intersectionRatio < 0.5) return;
        const name  = e.target.dataset.sectionName;
        const order = parseInt(e.target.dataset.sectionOrder, 10);
        const key   = name + '|' + (isNaN(order) ? '' : order);
        if (fired.has(key)) return;
        fired.add(key);

        window.trackedCapture('section viewed', {
          section_name: name,
          section_order: isNaN(order) ? null : order,
          page_path: window.location.pathname,
          page_type: document.body.dataset.pageType || null,
          service_type: document.body.dataset.serviceType || null
        });

        io.unobserve(e.target);
      });
    }, { threshold: 0.5 });

    sections.forEach(s => io.observe(s));
  }

  /* ────────────────────────────────────────────────
     page_left — best-effort exit tracking.
     IntersectionObserver tracks last section in view (>= 50% threshold);
     scroll listener tracks max scroll depth (% of page);
     pagehide listener fires posthog.capture('page left', {...}).
     Direct PostHog SDK call — no GTM trigger, no dataLayer hop.
     Caveat per posthog-hk-fintech skill: best-effort, mobile/fast-close
     may drop; trend analysis only, not exit-precision.
  ──────────────────────────────────────────────── */
  function _setupPageLeftTracking() {
    if (!window.trackedCapture) return;

    const startTs = Date.now();
    let lastSection = null;
    let maxScroll = 0;

    /* Track last section via IntersectionObserver (50% threshold) */
    const sections = document.querySelectorAll('[data-section-name]');
    if (sections.length && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(entries => {
        entries.forEach(e => {
          if (e.isIntersecting && e.intersectionRatio >= 0.5) {
            lastSection = e.target.dataset.sectionName;
          }
        });
      }, { threshold: 0.5 });
      sections.forEach(s => io.observe(s));
    }

    /* Track max scroll depth (passive listener for performance) */
    window.addEventListener('scroll', () => {
      const doc = document.documentElement;
      const total = doc.scrollHeight || 1;
      const pct = Math.round(Math.min((window.scrollY + window.innerHeight) / total, 1) * 100);
      if (pct > maxScroll) maxScroll = pct;
    }, { passive: true });

    /* Fire on pagehide (more reliable than beforeunload, esp. mobile) */
    window.addEventListener('pagehide', () => {
      window.trackedCapture('page left', {
        last_section_viewed: lastSection,
        max_scroll_depth: maxScroll,
        time_on_page_seconds: Math.round((Date.now() - startTs) / 1000),
        page_path: window.location.pathname,
        page_type: document.body.dataset.pageType || null,
        service_type: document.body.dataset.serviceType || null
      });
    });
  }

  let _installed = false;
  function installTrackingObservers() {
    if (_installed) return;
    _installed = true;
    _setupLeadFormStartedTracking();
    _setupSectionViewedTracking();
    _setupPageLeftTracking();
  }

  root.installTrackingObservers = installTrackingObservers;

  if (typeof document !== 'undefined' && document.addEventListener) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', installTrackingObservers);
    } else {
      installTrackingObservers();
    }
  }
})(typeof window !== 'undefined' ? window : globalThis);
